/**
 * Handles cross-domain user tracking by appending Amplitude analytics identifiers to links.
 *
 * When users click links to our other domains, this component automatically adds the current
 * Amplitude session ID and device ID as URL parameters. Those are parsed by the amplitude SDK and
 * enable consistent analytics tracking as users navigate between our different sites by maintaining
 * the same identifiers across domains.
 */
export default class AmplitudeLinkTagger {
	private domainsServedByOtherSites!: string[];
	private sessionId: number;
	private deviceId: string;

	public constructor(domainsServedByOtherSites: string[], sessionId: number, deviceId: string) {
		this.domainsServedByOtherSites = domainsServedByOtherSites;
		this.sessionId = sessionId;
		this.deviceId = deviceId;
		// Add event listeners for all types of link interactions:
		// - Regular left-click via 'click'
		// - Middle-click via 'auxclick'
		// - Right-click menu via 'contextmenu'
		document.addEventListener("click", this.handleLinkClick);
		document.addEventListener("auxclick", this.handleLinkClick);
		document.addEventListener("contextmenu", this.handleLinkClick);
	}

	/**
	 * Handles click events on links to intercept cross-domain navigation and append Amplitude tracking parameters.
	 *
	 * This function:
	 * 1. Traverses the event path to find the innermost anchor element that was clicked
	 * 2. Checks if the link points to one of our served domains
	 * 3. Appends the current Amplitude device ID and session ID as URL parameters
	 * 4. Skips links that already have these parameters to avoid duplicate modifications
	 *
	 * This ensures consistent user tracking across our different domains by maintaining
	 * the same Amplitude session and device identifiers when users navigate between sites.
	 */
	private handleLinkClick = (e: MouseEvent): void => {
		const path = e.composedPath();
		let anchor: HTMLAnchorElement | null = null;

		for (const element of path) {
			if (element instanceof HTMLAnchorElement) {
				anchor = element;
				break;
			}
		}

		if (!anchor || !anchor.href) {
			return;
		}

		const url = new URL(anchor.href);

		if (!url.host) {
			return;
		}

		if (!this.domainsServedByOtherSites.includes(url.host)) {
			return;
		}

		if (url.searchParams.has("ampDeviceId")) {
			return;
		}

		if (!this.deviceId || !this.sessionId) {
			return;
		}

		url.searchParams.set("ampDeviceId", this.deviceId);
		url.searchParams.set("ampSessionId", this.sessionId.toString());
		anchor.href = url.toString();
	};
}
